import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Sparkles, ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) {
      toast.success("Welcome back");
      const role = res.user?.role;
      navigate(role === "client" ? "/portal" : "/");
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F5F7FB]">
      {/* Left: brand story */}
      <div className="hidden lg:flex relative overflow-hidden border-r border-[#E4E7EC]">
        <div className="absolute inset-0 grid-lines opacity-40" />
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-orange-500/20 blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-red-500/10 blur-3xl" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-red-600 grid place-items-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="font-bold tracking-tight text-lg">Shloka<span className="text-orange-400">SMM</span></div>
          </div>
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-orange-400 mb-4">Agency Command Center</div>
            <h1 className="text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05]">
              One workspace<br/>for every client,<br/>every post, <span className="text-orange-400">every day.</span>
            </h1>
            <p className="mt-6 text-neutral-600 max-w-md">Run your entire social media agency: clients, calendars, approvals, creatives and AI-crafted captions — all under one roof.</p>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[["19","Client Fields"],["11","Content Types"],["AI×2","Caption Models"]].map(([n,l])=> (
              <div key={l} className="panel p-4">
                <div className="text-2xl font-bold">{n}</div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <form onSubmit={submit} className="w-full max-w-sm panel p-8" data-testid="login-form">
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-orange-400 mb-2">Sign in</div>
          <h2 className="text-2xl font-bold tracking-tight mb-6">Welcome back</h2>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs uppercase font-mono tracking-wider text-neutral-600">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e)=>setEmail(e.target.value)} data-testid="login-email" className="bg-[#FFFFFF] border-[#E4E7EC]" required />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs uppercase font-mono tracking-wider text-neutral-600">Password</Label>
                <Link to="/forgot-password" className="text-xs text-orange-600 hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
              <Input id="password" type="password" value={password} onChange={(e)=>setPassword(e.target.value)} data-testid="login-password" className="bg-[#FFFFFF] border-[#E4E7EC]" required />
            </div>
            <Button type="submit" disabled={busy} data-testid="login-submit" className="w-full bg-orange-500 hover:bg-orange-600 text-white">
              {busy ? "Signing in..." : (<>Sign in <ArrowRight className="w-4 h-4 ml-1"/></>)}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
